import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  type FunnelPageType,
  type FunnelPageVersion,
  type FunnelPageVersionSummary,
  FUNNEL_LABELS,
  getFunnelVersion,
  listFunnelVersions,
  restoreFunnelVersion,
  type VersionSource,
} from './api';
import { ConfirmDialog, formatBerlin, Loading, shortHash } from './ui';

/**
 * Reiter „Versionen" (Spec § 2): jeder Schreibvorgang legt einen vollen Inhalts-Schnappschuss an.
 * Vorschau = gespeicherter Body der Version im abgeschotteten iframe (sandbox ohne Rechte, also
 * ohne Skripte) — Rohinhalt ohne Seitenrahmen, keine Server-Renderung. Wiederherstellen setzt nur
 * Inhaltsfelder (nicht Strecken-Einstellungen, nicht den Publish-Status) und legt selbst eine
 * Version `restore` an; live geht es erst mit dem naechsten Strecken-Publish.
 */

const QUELLE: Record<VersionSource, string> = {
  bestand: 'Bestand (vor erster Änderung)',
  upload: 'Upload',
  editor: 'Seiten-Editor',
  code: 'HTML / Felder',
  settings: 'Einstellungen',
  restore: 'Wiederhergestellt',
};

// Die TABELLE haelt den Text schwarz: Theme-Grau ist invertiert (.claude/rules/react-frontend.md § Table styling).
const headSx = {
  backgroundColor: '#e0e0e0 !important',
  color: 'black',
  fontWeight: 'bold',
} as const;
const cellSx = { color: 'black' } as const;

interface Props {
  webinarId: string;
  pageType: FunnelPageType;
  /** Wechselt nach jedem Speichern im Dialog → Liste neu laden. */
  reloadKey: number;
  onRestored: (label: string, warnings: string[]) => void;
}

export default function VersionsPanel({ webinarId, pageType, reloadKey, onRestored }: Props) {
  const [versions, setVersions] = useState<FunnelPageVersionSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<FunnelPageVersion | null>(null);
  const [previewBusy, setPreviewBusy] = useState<number | null>(null);
  const [restore, setRestore] = useState<FunnelPageVersionSummary | null>(null);
  const [restoreBusy, setRestoreBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setVersions(null);
    setError(null);
    (async () => {
      const res = await listFunnelVersions(webinarId, pageType);
      if (cancelled) return;
      if (!res.success || !res.data) {
        setError(res.error || 'Versionen konnten nicht geladen werden');
        setVersions([]);
        return;
      }
      setVersions(res.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [webinarId, pageType, reloadKey]);

  const openPreview = async (v: FunnelPageVersionSummary) => {
    setPreviewBusy(v.id);
    setError(null);
    const res = await getFunnelVersion(webinarId, pageType, v.id);
    setPreviewBusy(null);
    if (!res.success || !res.data) {
      setError(res.error || `Version ${v.id} konnte nicht geladen werden`);
      return;
    }
    setPreview(res.data);
  };

  const doRestore = async () => {
    if (!restore) return;
    setRestoreBusy(true);
    setError(null);
    const res = await restoreFunnelVersion(webinarId, pageType, restore.id);
    setRestoreBusy(false);
    setRestore(null);
    if (!res.success || !res.data) {
      setError(res.error || 'Wiederherstellen fehlgeschlagen');
      return;
    }
    const w = [...(res.data.warnings || [])];
    if (!res.data.hashGleich) {
      w.push(
        'Der wiederhergestellte Inhalt ist nicht bytegleich mit der Quellversion (Sanitizer hat nachgebessert).',
      );
    }
    onRestored(
      `Version ${restore.id} wiederhergestellt (neue Version ${res.data.versionId}) — live erst nach Prüfen und Veröffentlichen der Strecke`,
      w,
    );
  };

  if (!versions) return <Loading label="Versionen laden …" />;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {error && <Alert severity="error">{error}</Alert>}
      {versions.length === 0 && !error && (
        <Alert severity="info">Noch keine Versionen für {FUNNEL_LABELS[pageType]}.</Alert>
      )}
      {versions.length > 0 && (
        <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', maxHeight: 460 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {['#', 'Quelle', 'Von', 'Zeit', 'Inhalt', ''].map((h) => (
                  <TableCell key={h} sx={headSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {versions.map((v, i) => (
                <TableRow key={v.id} hover>
                  <TableCell sx={cellSx}>
                    {v.id}
                    {i === 0 && <Chip size="small" label="neueste" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {v.report?.quelle.art === 'kopie' ? 'Kopie' : QUELLE[v.source] || v.source}
                    {v.restoredFrom != null && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        aus Version {v.restoredFrom}
                      </Typography>
                    )}
                    {v.report && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {v.report.quelle.htmlDatei || v.report.quelle.art} ·{' '}
                        {v.report.fehlend.length} fehlend · {v.report.umgezogen.length} Dateien
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={cellSx}>{v.createdBy || '—'}</TableCell>
                  <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                    {formatBerlin(v.createdAt)}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, fontFamily: 'monospace', fontSize: 12 }}>
                    {shortHash(v.contentHash)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Button
                      size="small"
                      onClick={() => openPreview(v)}
                      disabled={previewBusy !== null}
                    >
                      {previewBusy === v.id ? 'Lädt …' : 'Ansehen'}
                    </Button>
                    <Button size="small" onClick={() => setRestore(v)} disabled={restoreBusy}>
                      Wiederherstellen
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmDialog
        open={!!restore}
        title={`Version ${restore?.id ?? ''} wiederherstellen?`}
        confirmLabel="Wiederherstellen"
        busy={restoreBusy}
        onConfirm={doRestore}
        onCancel={() => setRestore(null)}
      >
        <Typography variant="body2">
          Der Entwurf von {FUNNEL_LABELS[pageType]} wird auf den Stand vom{' '}
          {formatBerlin(restore?.createdAt)} ({restore ? QUELLE[restore.source] : ''}) gesetzt. Der
          heutige Stand bleibt als Version erhalten. Stichtag, Upsell-Produkt und der Publish-Status
          bleiben unberührt — live geht der Stand erst mit den Schritten „Prüfen“ und „Veröffentlichen“.
        </Typography>
      </ConfirmDialog>

      <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Version {preview?.id} — {preview ? QUELLE[preview.source] : ''}
          <Typography variant="caption" display="block" color="text.secondary">
            {formatBerlin(preview?.createdAt)} · {preview?.createdBy || '—'} · Rohinhalt ohne
            Seitenrahmen, Skripte aus
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {preview && (
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography variant="body2">
                Titel: <b>{preview.content.headline || '—'}</b>
                {preview.content.plx_campaign_id && (
                  <> · Kampagne {preview.content.plx_campaign_id}</>
                )}
                {preview.content.price_text && <> · Preiszeile: {preview.content.price_text}</>}
              </Typography>
              <Box
                component="iframe"
                title={`Version ${preview.id}`}
                sandbox=""
                srcDoc={preview.content.body_html || '<p>(leer)</p>'}
                sx={{
                  width: '100%',
                  height: '65vh',
                  border: '1px solid #ccc',
                  backgroundColor: '#fff',
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
